import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Modal, Spin, Tag, Typography, Divider, Button } from "antd";
import approvalApi from "../../api/approvalApi";
import ApprovalTimeline from "./ApprovalTimeline";
import { offerChainRestart } from "./RestartChainPrompt";

const { Text } = Typography;

/** Same color convention as every legacy status column in this app. */
const STATUS_TAG = {
  PENDING: { color: "orange", label: "Pending" },
  APPROVED: { color: "green", label: "Approved" },
  REJECTED: { color: "red", label: "Rejected" },
  CANCELLED: { color: "red", label: "Cancelled" },
  WITHDRAWN: { color: "red", label: "Withdrawn" },
};

/**
 * A Status-column cell for an engine-managed row: looks exactly like the
 * plain legacy status Tag (same colours, same "Pending"/"Approved"/"Rejected"
 * wording) rather than the more detailed "Level 2/2" chip, so it sits
 * consistently among rows that are still on the legacy path. Clicking it
 * opens the full per-level breakdown - who's approved, who's still pending,
 * who rejected, and any override/bypass - which is where that detail now
 * lives instead of inline.
 *
 * Renders nothing when `progress` is null, so a module/company still on the
 * legacy path (or a row with no chain, e.g. before self-review) sees no
 * change here at all.
 *
 * `label`/`color` let a module keep its own status wording on the chip while
 * still opening the shared timeline - Separation says "Accepted" where the
 * engine says "Approved", and renaming it in that column would read as a
 * behaviour change to anyone using the screen.
 */
const ApprovalStatusCell = ({ progress, label, color }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [restarting, setRestarting] = useState(false);

  // Same pattern as AddLeave.js's Admin check - role name straight off the
  // logged-in user, not a permission lookup, matching how this one
  // deliberately-narrower-than-usual (Admin-only, not Admin+HR) action is
  // gated on the server side too (see approvalRequest.js's `isAdminOnly`).
  const { authUser } = useSelector(({ auth }) => auth);
  const isAdmin = authUser?.role_id?.role_name === "Admin";

  if (!progress) return null;

  const fallbackTag = STATUS_TAG[progress.status] || STATUS_TAG.PENDING;
  const tag = { color: color || fallbackTag.color, label: label || fallbackTag.label };

  const fetchDetail = async (requestId) => {
    if (!requestId) return;
    setLoading(true);
    const response = await approvalApi.getDetail(requestId);
    setLoading(false);
    if (response.status === 200) setDetail(response.data);
  };

  const openDetail = async () => {
    setOpen(true);

    // A synthesized legacy timeline has no approval_requests row behind it -
    // everything there is to show is already in `progress`, so skip the fetch
    // rather than firing a request that can only 404.
    if (!progress.requestId) return;

    await fetchDetail(progress.requestId);
  };

  // What actually renders below: the freshly-fetched detail once it lands,
  // the raw list-row `progress` prop until then - the same fallback the
  // timeline already used inline, lifted up here so the Admin-only restart
  // action is gated on the same data currently on screen. That matters after
  // a restart specifically: the old `progress` prop keeps pointing at the id
  // this cell was opened with, but `detail` gets refetched against the new
  // chain's id (see handleRestart below), so gating off `effectiveProgress`
  // rather than the raw prop is what lets a second restart, if ever needed,
  // target the fresh chain instead of the now-cancelled old one.
  const effectiveProgress = detail?.approval_progress || progress;

  const canOfferRestart =
    isAdmin && effectiveProgress?.status === "PENDING" && Boolean(effectiveProgress?.requestId);

  const handleRestart = () => {
    offerChainRestart({
      requestId: effectiveProgress.requestId,
      onBusy: setRestarting,
      // Restart closes the old request and opens a brand-new one at Level 1
      // under a different _id - re-fetching the id this cell was opened with
      // would just show the now-cancelled old chain. Refetch with the new id
      // instead, so the Admin immediately sees the fresh Level-1 state.
      onApplied: (result) => fetchDetail(result?.newRequestId),
    });
  };

  return (
    // Every click that originates here is claimed, so a Table with a row-level
    // onClick (the separation listings open a "Reasons" modal that way) does not
    // also fire. This has to wrap the Modal too, not just the Tag: antd renders
    // the Modal through a portal, and React synthetic events bubble along the
    // React tree rather than the DOM one - so without this, clicking anywhere
    // inside the open modal, including its close button, still reaches the row
    // handler and pops the row's own modal open behind this one.
    <span onClick={(event) => event.stopPropagation()}>
      <Tag color={tag.color} style={{ cursor: "pointer" }} onClick={openDetail}>
        {tag.label}
      </Tag>

      <Modal
        title="Approval status"
        open={open}
        onCancel={() => {
          setOpen(false);
          setDetail(null);
        }}
        footer={null}
        destroyOnClose
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <>
            {detail?.summary && (
              <Text strong style={{ display: "block", marginBottom: 16 }}>
                {detail.summary}
              </Text>
            )}
            <ApprovalTimeline progress={effectiveProgress} />

            {canOfferRestart && (
              <>
                <Divider style={{ margin: "16px 0 12px" }} />
                <Button
                  type="link"
                  danger
                  className="aw-restart-trigger"
                  loading={restarting}
                  onClick={handleRestart}
                >
                  Admin: Discard progress and restart at Level 1
                </Button>
              </>
            )}
          </>
        )}
      </Modal>
    </span>
  );
};

export default ApprovalStatusCell;
